// hooks/useAccessControlBatch.js
import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useAxios from "../utils/useAxios";
import { permissionsKey, retryPermissionsRequest } from "../utils/permissionKeys";

const DEFAULT_PERMISSIONS = {
  can_view: false,
  can_add: false,
  can_edit: false,
  can_delete: false,
};

export const useBatchAccessControl = (items = [], options = {}) => {
  const api = useAxios();
  const queryClient = useQueryClient();
  const { prefetchOnly = false, priority = "normal" } = options;

  // Stable reference for items array
  const stableItems = useMemo(
    () => items.filter((i) => i.contentType && i.objectId),
    [JSON.stringify(items)],
  );

  // Identify uncached items
  const uncachedItems = useMemo(() => {
    return stableItems.filter(({ contentType, objectId }) => {
      const cached = queryClient.getQueryData(permissionsKey(contentType, objectId));
      return !cached;
    });
  }, [stableItems, queryClient]);

  // Batch query key
  const batchKey = useMemo(
    () =>
      uncachedItems
        .map((i) => `${i.contentType}:${i.objectId}`)
        .sort()
        .join(","),
    [uncachedItems],
  );

  const { isLoading, error } = useQuery({
    queryKey: ["permissions-batch", batchKey],
    enabled: uncachedItems.length > 0 && !prefetchOnly,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: retryPermissionsRequest,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    queryFn: async ({ signal }) => {
      const payload = uncachedItems.map(({ contentType, objectId }) => ({
        content_type: contentType,
        object_id: objectId,
      }));

      const response = await api.post("/get-user-permissions/bulk/", payload, {
        signal,
      });

      // Populate individual caches
      Object.entries(response.data).forEach(([key, perms]) => {
        const [contentType, objectId] = key.split(":");
        const accessLevel = perms.can_delete
          ? "Owner"
          : perms.can_edit
            ? "Editor"
            : perms.can_view
              ? "Viewer"
              : null;

        queryClient.setQueryData(
          permissionsKey(contentType, objectId),
          {
            permissions: perms,
            access_level: accessLevel,
          },
        );
      });

      return response.data;
    },
  });

  // Prefetch function for proactive loading
  const prefetchPermissions = useMemo(
    () => async (itemsToPrefetch = []) => {
      const uncached = itemsToPrefetch.filter(({ contentType, objectId }) => {
        const cached = queryClient.getQueryData(permissionsKey(contentType, objectId));
        return !cached;
      });

      if (uncached.length === 0) return;

      // Goes through queryClient.fetchQuery (same key shape/options as the
      // useQuery above) instead of a raw api.post, so concurrent calls for
      // the same uncached set share one in-flight request instead of each
      // firing its own bulk POST - a real gap before this: calling
      // prefetchPermissions twice back-to-back (e.g. fast tab-switching, or
      // React 18 StrictMode's dev-mode double-invoke) fired two identical
      // requests, since a plain async function isn't deduped by React Query
      // on its own.
      const prefetchBatchKey = uncached
        .map(({ contentType, objectId }) => `${contentType}:${objectId}`)
        .sort()
        .join(",");

      try {
        const data = await queryClient.fetchQuery({
          queryKey: ["permissions-batch", prefetchBatchKey],
          staleTime: 10 * 60 * 1000,
          gcTime: 30 * 60 * 1000,
          queryFn: async () => {
            const payload = uncached.map(({ contentType, objectId }) => ({
              content_type: contentType,
              object_id: objectId,
            }));

            const response = await api.post(
              "/get-user-permissions/bulk/",
              payload,
            );

            return response.data;
          },
        });

        Object.entries(data).forEach(([key, perms]) => {
          const [contentType, objectId] = key.split(":");
          const accessLevel = perms.can_delete
            ? "Owner"
            : perms.can_edit
              ? "Editor"
              : perms.can_view
                ? "Viewer"
                : null;

          queryClient.setQueryData(
            permissionsKey(contentType, objectId),
            {
              permissions: perms,
              access_level: accessLevel,
            },
          );
        });
      } catch (error) {
        console.error("Prefetch permissions error:", error);
      }
    },
    [api, queryClient],
  );

  return {
    isLoading,
    error,
    prefetchPermissions,
    cachedCount: stableItems.length - uncachedItems.length,
    uncachedCount: uncachedItems.length,
  };
};